import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  ArrowLeft,
  AlertTriangle,
  Trash2,
  Save,
  Calendar,
  BookOpen,
  Plus,
  Minus,
  ChevronDown,
  Upload,
  ExternalLink,
} from 'lucide-react';
import { useApp } from '@/store';
import { Billing } from '@/data/types';
import { useT } from '@/lib/i18n';
import { fmtCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import type { Extinguisher } from '@/data/types';

const typeDocs: Record<string, Record<string, string>> = {
  'schaum-ab': {
    de: 'Schaum-Feuerlöscher Aufladedruck (AB): Für Brände der Klassen A und B. Jährliche Sichtkontrolle, alle 3 Jahre Hauptwartung, Schaummittel alle 6 Jahre erneuern.',
    en: 'Foam extinguisher boost pressure (AB): For class A & B fires. Annual visual check, full service every 3 years, replace foam every 6 years.',
  },
  'pulver-ab': {
    de: 'Pulver-Feuerlöscher Aufladedruck (AB): Für Klassen A, B, C. Altersgrenze 23 Jahre. Achtung: Pulver kann verklumpen — Rütteltest Pflicht.',
    en: 'Powder extinguisher boost pressure (AB): Class A, B, C. Age limit 23 years. Caution: powder may cake — shake test required.',
  },
  co2: {
    de: 'CO2-Feuerlöscher: Für Klasse B und elektrische Anlagen. Rückstandsfrei. Gewicht vor/nach Service prüfen. Altersgrenze 20 Jahre.',
    en: 'CO2 extinguisher: Class B & electrical. Residue-free. Weigh before/after service. Age limit 20 years.',
  },
  loeschposten: {
    de: 'Löschposten nach SN EN 671-3: Jährliche Kontrolle, alle 5 Jahre Druckprüfung mit 12 Bar. Schlauch visuell prüfen auf Knicke und Risse.',
    en: 'Fire hose per SN EN 671-3: Annual check, 5-year 12 Bar pressure test. Visually inspect hose for kinks and cracks.',
  },
  'fettbrand-ab': {
    de: 'Fettbrand-Feuerlöscher (Klasse F): Für Speiseöle und -fette. Niemals Wasser verwenden. Spezielles Saponifikationsmittel.',
    en: 'Fat-fire extinguisher (class F): For cooking oils and fats. Never use water. Special saponification agent.',
  },
  'lithium-ab': {
    de: 'Lithium-Feuerlöscher: Für Li-Ion-Akkus. Kühlwirkung kritisch. Spezielles Löschmittel. Altersgrenze 18 Jahre.',
    en: 'Lithium extinguisher: For Li-ion batteries. Cooling effect critical. Special agent. Age limit 18 years.',
  },
};

export function ExtinguisherEdit() {
  const { id, extId } = useParams();
  const nav = useNavigate();
  const t = useT();
  const lang = useApp(s => s.lang);
  const customer = useApp(s => s.customers.find(c => c.id === id));
  const mfrs = useApp(s => s.manufacturers);
  const types = useApp(s => s.deviceTypes);
  const spareParts = useApp(s => s.spareParts);
  const settings = useApp(s => s.settings);
  const toast = useApp(s => s.toast);
  const upsertExt = useApp(s => s.upsertExtinguisher);
  const deleteExt = useApp(s => s.deleteExtinguisher);
  const existing = customer?.extinguishers.find(e => e.id === extId);

  const [mfrId, setMfrId] = useState(
    mfrs.find(m => m.name === existing?.mfr)?.id ?? ''
  );
  const [modelId, setModelId] = useState(
    mfrs.flatMap(m => m.models).find(m => m.name === existing?.model)?.id ?? ''
  );
  const [mfrOpen, setMfrOpen] = useState(false);
  const [mfrSearch, setMfrSearch] = useState('');
  const [docPdf, setDocPdf] = useState(existing?.documentationPdf ?? '');
  const docInputRef = useRef<HTMLInputElement>(null);
  const [floor, setFloor] = useState(existing?.floor ?? '');
  const [location, setLocation] = useState(existing?.location ?? '');
  const [deviceNumber, setDeviceNumber] = useState(
    existing?.deviceNumber ?? ''
  );
  const [year, setYear] = useState(existing?.year?.toString() ?? '');
  const [lastInsp, setLastInsp] = useState(existing?.lastInspection ?? '');
  const [nextInsp, setNextInsp] = useState(existing?.nextInspection ?? '');
  const [refilled, setRefilled] = useState(existing?.refilled ?? '');
  const [billing, setBilling] = useState(existing?.billing ?? Billing.NONE);
  const [selectedParts, setSelectedParts] = useState<string[]>(
    existing?.spareParts ?? []
  );

  const models = mfrs.find(m => m.id === mfrId)?.models ?? [];
  const selectedMfr = mfrs.find(m => m.id === mfrId);
  const selectedModel = models.find(m => m.id === modelId);
  const type = types.find(x => x.id === selectedModel?.deviceType);
  const filteredMfrs = useMemo(
    () =>
      mfrs.filter(m => m.name.toLowerCase().includes(mfrSearch.toLowerCase())),
    [mfrs, mfrSearch]
  );

  const ageWarning = useMemo(() => {
    if (!year || !type) return null;
    const age = new Date().getFullYear() - parseInt(year, 10);
    if (age > type.ageLimit)
      return { age, limit: type.ageLimit, cls: type.pressureClass };
    return null;
  }, [year, type]);

  const yearInvalid = useMemo(() => {
    if (!year) return false;
    const y = parseInt(year, 10);
    if (isNaN(y)) return true;
    const now = new Date().getFullYear();
    return y < now - 50 || y > now;
  }, [year]);

  const setToday = (setter: (v: string) => void) =>
    setter(new Date().toISOString().slice(0, 10));
  const computeNext = () => {
    if (lastInsp) {
      const d = new Date(lastInsp);
      d.setFullYear(d.getFullYear() + settings.inspectionInterval);
      setNextInsp(d.toISOString().slice(0, 10));
    }
  };

  if (!customer) return <div>{t('common.notFound')}</div>;

  const aggregatedParts = selectedParts.reduce<Record<string, number>>(
    (acc, a) => {
      acc[a] = (acc[a] ?? 0) + 1;
      return acc;
    },
    {}
  );

  const save = () => {
    if (!selectedMfr || !selectedModel || !type) {
      toast('Select manufacturer and model');
      return;
    }
    if (yearInvalid) {
      toast('Invalid manufacturing year');
      return;
    }
    const ext: Extinguisher = {
      id: existing?.id ?? `E-${Date.now()}`,
      floor,
      location,
      mfr: selectedMfr.name,
      model: selectedModel.name,
      content: selectedModel.content,
      type: type.id,
      deviceNumber,
      year: year ? parseInt(year, 10) : undefined,
      lastInspection: lastInsp || undefined,
      nextInspection: nextInsp || undefined,
      refilled: refilled || undefined,
      billing,
      spareParts: selectedParts,
      notes: existing?.notes,
      section: existing?.section,
      documentationPdf: docPdf || undefined,
    };
    upsertExt(customer.id, ext);
    toast(existing ? 'Updated' : 'Created');
    nav(`/customer/${customer.id}/extinguishers`);
  };

  const handleDelete = () => {
    if (!existing) return;
    if (!confirm('Delete extinguisher?')) return;
    deleteExt(customer.id, existing.id);
    toast('Deleted');
    nav(`/customer/${customer.id}/extinguishers`);
  };

  const docs = type ? typeDocs[type.id] : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
      data-tour="extinguisher-edit"
    >
      <div>
        <Link
          to={`/customer/${customer.id}/extinguishers`}
          className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> {t('common.back')}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight mt-2">
          {existing ? t('common.edit') : t('common.new')} {t('ext.title')}
        </h1>
        {existing && (
          <p className="text-sm text-muted-foreground font-mono">
            {existing.id}
          </p>
        )}
      </div>

      {ageWarning && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="pt-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <div className="text-sm">
              <span className="font-semibold text-amber-700 dark:text-amber-300">
                {ageWarning.age}y
              </span>{' '}
              <span>
                {t('ext.ageWarning')}: {ageWarning.limit}y ({ageWarning.cls})
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('ext.type')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>{t('ext.manufacturer')}</Label>
                <div className="relative" data-focus="mfr-search">
                  <button
                    data-testid="ext-mfr-trigger"
                    type="button"
                    onClick={() => setMfrOpen(o => !o)}
                    className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-accent"
                  >
                    <span
                      className={selectedMfr ? '' : 'text-muted-foreground'}
                    >
                      {selectedMfr?.name ?? t('ext.manufacturer')}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </button>
                  {mfrOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow-md">
                      <Input
                        placeholder={t('ext.mfrSearch')}
                        value={mfrSearch}
                        onChange={e => setMfrSearch(e.target.value)}
                        className="rounded-b-none border-0 border-b focus-visible:ring-0"
                        autoFocus
                      />
                      <div className="max-h-48 sm:max-h-64 overflow-auto">
                        {filteredMfrs.map(m => (
                          <button
                            key={m.id}
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm hover:bg-accent"
                            onClick={() => {
                              setMfrId(m.id);
                              setModelId('');
                              setMfrSearch('');
                              setMfrOpen(false);
                            }}
                          >
                            {m.name}
                          </button>
                        ))}
                        {filteredMfrs.length === 0 && (
                          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                            {t('common.notFound')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Label>{t('ext.model')}</Label>
                <Select
                  value={modelId}
                  onValueChange={setModelId}
                  disabled={!mfrId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('ext.model')} />
                  </SelectTrigger>
                  <SelectContent>
                    {models.map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name} — {m.content}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedModel && type && (
                <div className="rounded-md bg-primary/5 p-3 text-xs space-y-1">
                  <div className="font-semibold">{selectedModel.name}</div>
                  <div>
                    {t('ext.content')}: {selectedModel.content}
                  </div>
                  <div>
                    {t('ext.type')}: {type.nameDe} ({type.nameEn})
                  </div>
                  <div>
                    {t('ext.pressure')}:{' '}
                    <Badge variant="outline">{type.pressureClass}</Badge>
                  </div>
                  <div>
                    {t('ext.ageLimit')}: {type.ageLimit}y
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {docs && (
            <Card className="border-primary/20">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-primary" />
                  {t('ext.documentation')}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-xs leading-relaxed text-muted-foreground">
                {docs[lang] ?? docs.en}
              </CardContent>
            </Card>
          )}

          <Card data-focus="doc-upload">
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Upload className="h-4 w-4 text-primary" />
                {t('ext.uploadDoc')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <input
                ref={docInputRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => setDocPdf(reader.result as string);
                  reader.readAsDataURL(file);
                }}
              />
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => docInputRef.current?.click()}
                >
                  <Upload className="h-3 w-3" />
                  {t('ext.uploadDoc')}
                </Button>
                {docPdf && (
                  <Button variant="outline" size="sm" asChild>
                    <a href={docPdf} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3 w-3" />
                      {t('ext.viewDoc')}
                    </a>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('ext.placement')}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>{t('ext.floor')}</Label>
                <Input
                  data-testid="ext-floor"
                  value={floor}
                  onChange={e => setFloor(e.target.value)}
                />
              </div>
              <div>
                <Label>{t('ext.location')}</Label>
                <Input
                  data-testid="ext-location"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                />
              </div>
              <div>
                <Label>{t('ext.idNumber')}</Label>
                <Input
                  data-testid="ext-device-number"
                  value={deviceNumber}
                  onChange={e => setDeviceNumber(e.target.value)}
                />
              </div>
              <div>
                <Label>{t('ext.year')}</Label>
                <Input
                  data-testid="ext-year"
                  type="number"
                  value={year}
                  onChange={e => setYear(e.target.value)}
                  className={yearInvalid ? 'border-red-500' : ''}
                />
                {yearInvalid && (
                  <div className="mt-1 text-xs text-red-500">
                    {t('common.invalidYear')}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">
                {t('ext.inspectionDates')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>{t('ext.lastInspection')}</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="ext-last-inspection"
                    type="date"
                    value={lastInsp}
                    onChange={e => setLastInsp(e.target.value)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setToday(setLastInsp)}
                  >
                    <Calendar className="h-3 w-3" />
                    {t('common.today')}
                  </Button>
                </div>
              </div>
              <div>
                <Label>{t('ext.nextInspection')}</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="ext-next-inspection"
                    type="date"
                    value={nextInsp}
                    onChange={e => setNextInsp(e.target.value)}
                  />
                  <Button variant="outline" size="sm" onClick={computeNext}>
                    +{settings.inspectionInterval}y
                  </Button>
                </div>
              </div>
              <div>
                <Label>{t('ext.refilled')}</Label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={refilled}
                    onChange={e => setRefilled(e.target.value)}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setToday(setRefilled)}
                  >
                    <Calendar className="h-3 w-3" />
                    {t('common.today')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('ext.billing')}</CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={billing}
                onValueChange={v => setBilling(v as typeof billing)}
              >
                {[
                  { v: Billing.NONE, l: t('ext.billingNone') },
                  { v: Billing.NEW, l: t('ext.billingNew') },
                  { v: Billing.MAINTENANCE, l: t('ext.billingMaintenance') },
                  { v: Billing.DISPOSED, l: t('ext.billingDisposed') },
                ].map(o => (
                  <div key={o.v} className="flex items-center gap-2">
                    <RadioGroupItem value={o.v} id={`b-${o.v}`} />
                    <Label htmlFor={`b-${o.v}`} className="text-sm">
                      {o.l}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('ext.spareParts')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1 max-h-48 overflow-auto border rounded-md p-2">
                  {spareParts
                    .filter(p => p.type === 'extinguisher')
                    .map(p => (
                      <button
                        key={p.articleNr}
                        onClick={() =>
                          setSelectedParts([...selectedParts, p.articleNr])
                        }
                        className="w-full text-left text-xs px-2 py-1 rounded hover:bg-accent flex justify-between"
                      >
                        <span>
                          <span className="font-mono text-muted-foreground">
                            {p.articleNr}
                          </span>{' '}
                          {p.name}
                        </span>
                        <span className="text-muted-foreground">
                          {fmtCurrency(p.price, settings.currency)}
                        </span>
                      </button>
                    ))}
                </div>
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground mb-1">
                    {Object.keys(aggregatedParts).length} /{' '}
                    {selectedParts.length}
                  </div>
                  {Object.entries(aggregatedParts).map(([a, qty]) => {
                    const part = spareParts.find(p => p.articleNr === a);
                    return (
                      <div
                        key={a}
                        className="flex justify-between items-center text-xs px-2 py-2 sm:py-1 rounded bg-primary/5 gap-2"
                      >
                        <span className="flex-1 truncate">
                          {part?.name ?? a}
                        </span>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 sm:h-5 sm:w-5"
                            onClick={() => {
                              const idx = selectedParts.indexOf(a);
                              if (idx >= 0) {
                                const copy = [...selectedParts];
                                copy.splice(idx, 1);
                                setSelectedParts(copy);
                              }
                            }}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="font-mono w-5 text-center">
                            {qty}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 sm:h-5 sm:w-5"
                            onClick={() =>
                              setSelectedParts([...selectedParts, a])
                            }
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => nav(`/customer/${customer.id}/extinguishers`)}
        >
          {t('common.cancel')}
        </Button>
        {existing && (
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
            {t('common.delete')}
          </Button>
        )}
        <Button data-testid="ext-save" onClick={save}>
          <Save className="h-4 w-4" />
          {t('common.save')}
        </Button>
      </div>
    </motion.div>
  );
}
